/**
 * @voltxampere/engine — Power dissipation calculator (Sprint 59)
 *
 * Per-component instantaneous power dissipation from a DC operating
 * point. Pure post-solve compute — engine zero-diff invariant
 * preserved (Newton iteration sees no thermal feedback in S59 MVP).
 *
 * Resistor:        P = V_drop² / R = (V_p1 - V_p2)² / R
 * Diode (forward): P = V_forward × I
 * BJT collector:   P = V_CE × I_C  (approximate; emitter current
 *                  shares a small leg but the collector loop is
 *                  the dominant dissipator)
 * MOSFET drain:    P = V_DS × I_D
 *
 * Components without a recognised stamp type contribute 0 W. The
 * caller treats null / undefined as "no thermal model" rather than
 * "zero power".
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Walk the circuit and produce per-component power dissipation.
 * Returns a Map keyed by component id. Components with no thermal-
 * model coverage (op-amps, voltage sources, grounds) are omitted.
 */
export function computeComponentPower(circuit, dc) {
    const out = new Map();
    for (const c of circuit.components) {
        const p = powerForComponent(c, dc);
        if (p === null)
            continue;
        out.set(c.id, { componentId: c.id, type: c.type, powerW: p });
    }
    return out;
}
function powerForComponent(c, dc) {
    switch (c.type) {
        case "resistor":
            return powerResistor(c, dc);
        case "diode":
        case "led":
        case "zener":
            return powerDiode(c, dc);
        case "bjt-npn":
        case "bjt-pnp":
            return powerBjt(c, dc);
        case "nmos":
        case "pmos":
            return powerMosfet(c, dc);
        case "capacitor":
        case "inductor":
            // Energy storage; per-frame dissipation ≈ 0 in steady state.
            return 0;
        default:
            return null;
    }
}
function netVoltage(dc, netId) {
    if (netId === null || netId === undefined)
        return 0;
    const v = dc.nodeVoltages.get(netId);
    return typeof v === "number" && Number.isFinite(v) ? v : 0;
}
function netOf(c, terminalId) {
    return c.terminals.find((t) => t.terminalId === terminalId)?.netId ?? null;
}
function powerResistor(c, dc) {
    const r = asPositiveNumber(c.params.resistance);
    if (r === null)
        return 0;
    const v1 = netVoltage(dc, netOf(c, "p1"));
    const v2 = netVoltage(dc, netOf(c, "p2"));
    const dv = v1 - v2;
    return (dv * dv) / r;
}
function powerDiode(c, dc) {
    // Without saturation current + Shockley solve, use V_drop × I_branch
    // approximation. The branch current is read from sourceCurrents if a
    // current monitor is attached; otherwise we approximate from the
    // forward voltage drop and the standard 1 V_T model.
    const vA = netVoltage(dc, netOf(c, "anode"));
    const vK = netVoltage(dc, netOf(c, "cathode"));
    const vDrop = Math.max(0, vA - vK);
    // Branch current proxy — look up any source current that names this
    // component (S58a / S29 pattern). Otherwise leak ≈ 0.
    const i = dc.sourceCurrents.get(c.id);
    if (typeof i === "number" && Number.isFinite(i)) {
        return Math.max(0, vDrop * Math.abs(i));
    }
    return 0;
}
function powerBjt(c, dc) {
    const vC = netVoltage(dc, netOf(c, "collector"));
    const vE = netVoltage(dc, netOf(c, "emitter"));
    const vCE = c.type === "bjt-npn" ? vC - vE : vE - vC;
    const iC = dc.sourceCurrents.get(c.id);
    if (typeof iC === "number" && Number.isFinite(iC)) {
        return Math.max(0, Math.abs(vCE) * Math.abs(iC));
    }
    return 0;
}
function powerMosfet(c, dc) {
    const vD = netVoltage(dc, netOf(c, "drain"));
    const vS = netVoltage(dc, netOf(c, "source"));
    const vDS = c.type === "nmos" ? vD - vS : vS - vD;
    const iD = dc.sourceCurrents.get(c.id);
    if (typeof iD === "number" && Number.isFinite(iD)) {
        return Math.max(0, Math.abs(vDS) * Math.abs(iD));
    }
    return 0;
}
function asPositiveNumber(value) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0)
        return value;
    if (typeof value === "object" && value !== null && "value" in value) {
        const inner = value.value;
        if (typeof inner === "number" && Number.isFinite(inner) && inner > 0)
            return inner;
    }
    return null;
}
//# sourceMappingURL=power.js.map